import { ApolloLink, FetchResult, Observable, Operation } from '@apollo/client';

import { Authenticated, Permissions, createAuthHeaders } from '../AuthorizationProvider';
import { CustomSubscriptionClient } from './CustomSubscriptionClient';
import {
  createIntradayMeasurementsChangedSubscriptionDefinition,
  createRealTimeAdherenceChangedSubscriptionDefinition,
  createRealTimeAdherenceEventsChangedSubscriptionDefinition,
  createRealTimeAgentEventsChangedSubscriptionDefinition,
  createRealTimeScheduleChangedSubscriptionDefinition,
  createWebSocketStateSubscriptionDefinition,
} from './constants';

export class CustomWebSocketLink extends ApolloLink {
  private readonly subscriptionClient: CustomSubscriptionClient;

  private readonly authentication: Authenticated;

  private readonly tenant: string;

  constructor(url: string, tenant: string, auth: Authenticated) {
    super();

    this.authentication = auth;
    this.tenant = tenant;

    const tenantId = this.getHeaders()['Tenant-ID'];

    const subscriptionDefinitions = [
      createRealTimeScheduleChangedSubscriptionDefinition(tenantId),
      createRealTimeAdherenceChangedSubscriptionDefinition(tenantId),
      createRealTimeAdherenceEventsChangedSubscriptionDefinition(tenantId),
      createRealTimeAgentEventsChangedSubscriptionDefinition(tenantId),
      createWebSocketStateSubscriptionDefinition(),
      createIntradayMeasurementsChangedSubscriptionDefinition(tenantId),
    ];

    this.subscriptionClient = new CustomSubscriptionClient(
      url,
      process.env.NODE_ENV !== 'production',
      subscriptionDefinitions,
      () => this.getHeaders(),
      () => this.userHasPermissionsToOpenWS()
    );
  }

  private getHeaders() {
    return createAuthHeaders(this.authentication.keycloak.token!, this.tenant);
  }

  private userHasPermissionsToOpenWS() {
    return this.authentication.userHasPermissions(
      [Permissions.ViewRealTimeAdherenceEvents, Permissions.ViewIntradayEvents],
      false
    );
  }

  public request(operation: Operation): Observable<FetchResult> | null {
    // ignore request when user is not allowed to open ws
    if (!this.userHasPermissionsToOpenWS()) {
      // also destroy previously created subscriptionClient when client was created prior to role removal
      this.destroy();
      return null;
    }

    // if permissions are added after creating of subscriptionClient, it will handle opening of ws connection internally
    return this.subscriptionClient.request(operation);
  }

  public destroy() {
    return this.subscriptionClient.destroy();
  }

  public getSubscriptionClient() {
    return this.subscriptionClient;
  }
}
